import React, { memo, useCallback, useEffect, useMemo, useState } from 'react';

import { Box, Button } from '@mui/material';
import { AddBoxIcon } from '@sharedCR/coreDesignSystem/icons';
import TableCDS from '@sharedCR/coreDesignSystem/Table/TableCDS';
import { entitySideDataTableStyles } from 'app/shared/componentsRestyled/common/form/shared/styles';
import { CheckboxCDS } from 'app/shared/componentsRestyled/coreDesignSystem/form';
import _ from 'lodash';

import { IconSize } from 'constants/coreDesignSystem';
import useSort from 'hooks/useSort';
import { getConditionalObject } from 'utils/data';

import { PropertyAndLoanTestIds } from '../../../../PropertyAndLoan.testIds';
import { tableWrapperStyles } from '../../Content.styles';
import { getNotFoundTableData } from '../../Content.utils';
import {
  defaultForm,
  defaultInitialEntitySideData,
  pagesPropTypes,
} from '../MainContent.constants';

import { getAgentInfoTableConfig, getSellerInfoTableConfig } from './AgentAndSeller.config';
import {
  defaultListingAgentSortOrder,
  defaultSellerSortOrder,
  defaultSellingAgentSortOrder,
  TableDataType,
  TableDataTypeToSideDataNameMap,
} from './AgentAndSeller.constants';
import { addAnotherEntityStyles, containerStyles, sectionStyles } from './AgentAndSeller.styles';
import {
  checkIsAgentsMaxCount,
  createNewTableData,
  getInitialValuesMaps,
  normalizeFormData,
  orderTableData,
  persistAgentToCrm,
  prepareModifiedFields,
} from './AgentAndSeller.utils';
import ListingAgentForm from './ListingAgentForm';
import SellerForm from './SellerForm';
import SellingAgentForm from './SellingAgentForm';

const defaultPendingChanges = {};

const AgentAndSeller = ({
  isLoading,
  form = defaultForm,
  className,
  applicationData,
  setEntitySideData,
  setInitialEntitySideData,
  entitySideData,
  initialEntitySideData = defaultInitialEntitySideData,
  setIsSavingAllowed,
  pendingChanges,
  setIsNestedEntityEditActive,
}) => {
  const [editableSellingAgentData, setEditableSellingAgentData] = useState(null);
  const [editableListingAgentData, setEditableListingAgentData] = useState(null);
  const [editableSellerData, setEditableSellerData] = useState(null);

  const { order: sellingAgentOrder, onRequestSort: onRequestSellingAgentSort } = useSort(
    defaultSellingAgentSortOrder,
  );
  const { order: listingAgentOrder, onRequestSort: onRequestListingAgentSort } = useSort(
    defaultListingAgentSortOrder,
  );
  const { order: sellerOrder, onRequestSort: onRequestSellerSort } =
    useSort(defaultSellerSortOrder);

  const isFormDataEmpty = _.isEmpty(form);
  const entityData = isFormDataEmpty ? applicationData : form;
  const isDataEmpty = _.isEmpty(entityData);
  const isDataLoading = isLoading && isDataEmpty;
  const sellingAgentsTableData = applicationData?.sellingAgents;
  const listingAgentsTableData = applicationData?.listingAgents;
  const forSaleByOwner = applicationData?.forSaleByOwner;
  const sellersTableData = applicationData?.sellers;
  const pendingChangesData =
    pendingChanges?.agentsAndSellersPendingChanges || defaultPendingChanges;

  const { sellingAgents, listingAgents, sellers } = entitySideData;
  const isEditSellingAgentActive = !!editableSellingAgentData;
  const isEditListingAgentActive = !!editableListingAgentData;
  const isEditSellerActive = !!editableSellerData;
  const isSellingAgentsExist = !!sellingAgents?.length;
  const isListingAgentsExist = !!listingAgents?.length;
  const isSellersExist = !!sellers?.length;

  useEffect(() => {
    setIsSavingAllowed(
      !isEditSellingAgentActive && !isEditListingAgentActive && !isEditSellerActive,
    );
    setIsNestedEntityEditActive(
      isEditSellingAgentActive || isEditListingAgentActive || isEditSellerActive,
    );

    return () => {
      setIsSavingAllowed(true);
    };
  }, [
    isEditListingAgentActive,
    isEditSellerActive,
    isEditSellingAgentActive,
    setIsSavingAllowed,
    setIsNestedEntityEditActive,
  ]);

  const { sellingAgentInitialValuesMap, listingAgentInitialValuesMap, sellerInfoInitialValuesMap } =
    useMemo(() => {
      return getInitialValuesMaps({ initialEntitySideData });
    }, [initialEntitySideData]);

  const {
    orderedSellingAgentsWithPendingChanges,
    orderedListingAgentsWithPendingChanges,
    orderedSellersWithPendingChanges,
    isSellingAgentsMaxCount,
    isListingAgentsMaxCount,
  } = useMemo(() => {
    const orderedSellingAgents = orderTableData({ data: sellingAgents, order: sellingAgentOrder });
    const orderedListingAgents = orderTableData({ data: listingAgents, order: listingAgentOrder });
    const orderedSellers = orderTableData({ data: sellers, order: sellerOrder });

    const orderedSellingAgentsWithPendingChanges = prepareModifiedFields({
      currentData: orderedSellingAgents,
      pendingChangesData,
      initialValuesMap: sellingAgentInitialValuesMap,
    });
    const orderedListingAgentsWithPendingChanges = prepareModifiedFields({
      currentData: orderedListingAgents,
      pendingChangesData,
      initialValuesMap: listingAgentInitialValuesMap,
    });
    const orderedSellersWithPendingChanges = prepareModifiedFields({
      currentData: orderedSellers,
      pendingChangesData,
      initialValuesMap: sellerInfoInitialValuesMap,
    });

    const isSellingAgentsMaxCount = checkIsAgentsMaxCount(orderedSellingAgentsWithPendingChanges);
    const isListingAgentsMaxCount = checkIsAgentsMaxCount(orderedListingAgentsWithPendingChanges);

    return {
      orderedSellingAgentsWithPendingChanges,
      orderedListingAgentsWithPendingChanges,
      orderedSellersWithPendingChanges,
      isSellingAgentsMaxCount,
      isListingAgentsMaxCount,
    };
  }, [
    listingAgentInitialValuesMap,
    listingAgentOrder,
    listingAgents,
    sellerInfoInitialValuesMap,
    sellerOrder,
    sellers,
    sellingAgentInitialValuesMap,
    sellingAgentOrder,
    pendingChangesData,
    sellingAgents,
  ]);

  const correctedSellingAgentsTableData = isSellingAgentsExist
    ? orderedSellingAgentsWithPendingChanges
    : getNotFoundTableData('name');
  const correctedListingAgentsTableData = isListingAgentsExist
    ? orderedListingAgentsWithPendingChanges
    : getNotFoundTableData('name');
  const correctedSellersTableData = isSellersExist
    ? orderedSellersWithPendingChanges
    : getNotFoundTableData('sellerName');

  useEffect(() => {
    const isAgentAndSellerTableDataExist =
      !!sellingAgentsTableData && !!listingAgentsTableData && !!sellersTableData;

    if (!isAgentAndSellerTableDataExist) {
      return;
    }

    const isSellingAgentsExist = _.has(entitySideData, 'sellingAgents');
    const isListingAgentsExist = _.has(entitySideData, 'listingAgents');
    const isSellersExist = _.has(entitySideData, 'sellers');

    if (isSellingAgentsExist && isListingAgentsExist && isSellersExist) {
      return;
    }

    const dataToSet = {
      forSaleByOwner,
      ...getConditionalObject(!isSellingAgentsExist, {
        sellingAgents: sellingAgentsTableData,
      }),
      ...getConditionalObject(!isListingAgentsExist, {
        listingAgents: listingAgentsTableData,
      }),
      ...getConditionalObject(!isSellersExist, {
        sellers: sellersTableData,
      }),
    };

    setEntitySideData(prevState => ({
      ...prevState,
      ...dataToSet,
    }));
    setInitialEntitySideData(prevState => ({
      ...prevState,
      ...dataToSet,
    }));
  }, [
    entitySideData,
    forSaleByOwner,
    listingAgentsTableData,
    sellersTableData,
    sellingAgentsTableData,
    setEntitySideData,
    setInitialEntitySideData,
  ]);

  const setEditableTableData = useCallback(({ data, entityType }) => {
    switch (entityType) {
      case TableDataType.SELLING_AGENT:
        setEditableSellingAgentData(data);
        break;

      case TableDataType.LISTING_AGENT:
        setEditableListingAgentData(data);
        break;

      case TableDataType.SELLER:
        setEditableSellerData(data);
        break;

      default:
        break;
    }
  }, []);

  const deleteTableData = useCallback(
    ({ id, entityType }) => {
      const sideDataEntityName = TableDataTypeToSideDataNameMap[entityType];

      setEntitySideData(prevState => ({
        ...prevState,
        [sideDataEntityName]: prevState[sideDataEntityName].reduce((acc, entity) => {
          if (entity.id !== id) {
            acc.push(entity);

            return acc;
          }

          if (entity.isAdded) {
            return acc;
          }

          acc.push({ ...entity, isDeleted: true });

          return acc;
        }, []),
      }));
      setEditableTableData({ data: null, entityType });
    },
    [setEditableTableData, setEntitySideData],
  );

  const { sellingAgentTableConfig, listingAgentTableConfig, sellerTableConfig } = useMemo(() => {
    const sellingAgentTableConfig = getAgentInfoTableConfig({
      setEditableTableData,
      deleteTableData,
      editableRowId: editableSellingAgentData?.id,
      isActionDisabled: isLoading,
      entityType: TableDataType.SELLING_AGENT,
    });
    const listingAgentTableConfig = getAgentInfoTableConfig({
      setEditableTableData,
      deleteTableData,
      editableRowId: editableListingAgentData?.id,
      isActionDisabled: isLoading,
      entityType: TableDataType.LISTING_AGENT,
    });
    const sellerTableConfig = getSellerInfoTableConfig({
      setEditableTableData,
      deleteTableData,
      editableRowId: editableSellerData?.id,
      isActionDisabled: isLoading,
    });

    return {
      sellingAgentTableConfig,
      listingAgentTableConfig,
      sellerTableConfig,
    };
  }, [
    setEditableTableData,
    deleteTableData,
    editableSellingAgentData?.id,
    isLoading,
    editableListingAgentData?.id,
    editableSellerData?.id,
  ]);

  const addNewTableEntity = useCallback(
    e => {
      const { type } = e.target.dataset;
      const entityData = createNewTableData(type);

      const sideDataEntityName = TableDataTypeToSideDataNameMap[type];

      setEntitySideData(prevState => ({
        ...prevState,
        [sideDataEntityName]: [...(prevState[sideDataEntityName] ?? []), entityData],
      }));
      setEditableTableData({ data: entityData, entityType: type });
    },
    [setEntitySideData, setEditableTableData],
  );

  const cancelForm = useCallback(
    data => {
      if (data.isNewlyAdded) {
        const sideDataEntityName = TableDataTypeToSideDataNameMap[data.entityType];

        setEntitySideData(prevState => ({
          ...prevState,
          [sideDataEntityName]: prevState[sideDataEntityName].filter(({ id }) => id !== data.id),
        }));
      }

      setEditableTableData({ data: null, entityType: data.entityType });
    },
    [setEditableTableData, setEntitySideData],
  );

  const saveForm = useCallback(
    ({ updatedData, initialData }) => {
      const { entityType } = initialData;
      const sideDataEntityName = TableDataTypeToSideDataNameMap[entityType];
      const dataToSave = normalizeFormData({ form: updatedData, initialData });

      setEntitySideData(prevState => ({
        ...prevState,
        [sideDataEntityName]: prevState[sideDataEntityName].map(entity =>
          entity.id === dataToSave.id ? dataToSave : entity,
        ),
      }));
      setEditableTableData({ data: null, entityType });

      // Fire-and-forget: persist selling/listing agents to CRM so they appear
      // in the RealtorPicker on future loans.  Sellers are excluded — they map
      // to a different CRM contact type.
      if (
        entityType === TableDataType.SELLING_AGENT ||
        entityType === TableDataType.LISTING_AGENT
      ) {
        persistAgentToCrm(dataToSave);
      }
    },
    [setEditableTableData, setEntitySideData],
  );

  const onForSaleByOwnerChange = useCallback(
    () => setEntitySideData(prev => ({ ...prev, forSaleByOwner: !prev.forSaleByOwner })),
    [setEntitySideData],
  );

  const sellingAgentRowContent = editableSellingAgentData ? (
    <SellingAgentForm
      cancelForm={cancelForm}
      deleteSellingAgent={deleteTableData}
      isLoading={isLoading}
      pendingChanges={pendingChangesData[editableSellingAgentData.id]}
      saveForm={saveForm}
      sellingAgentData={editableSellingAgentData}
    />
  ) : null;

  const listingAgentRowContent = editableListingAgentData ? (
    <ListingAgentForm
      cancelForm={cancelForm}
      deleteListingAgent={deleteTableData}
      isLoading={isLoading}
      listingAgentData={editableListingAgentData}
      pendingChanges={pendingChangesData[editableListingAgentData.id]}
      saveForm={saveForm}
    />
  ) : null;

  const sellerRowContent = editableSellerData ? (
    <SellerForm
      cancelForm={cancelForm}
      deleteSeller={deleteTableData}
      isLoading={isLoading}
      pendingChanges={pendingChangesData[editableSellerData.id]}
      saveForm={saveForm}
      sellerData={editableSellerData}
    />
  ) : null;

  return (
    <Box className={className} sx={containerStyles}>
      <Box>
        <CheckboxCDS
          fullWidth
          data-testid={PropertyAndLoanTestIds.LISTING_AGENT_FORM_FOR_SALE_BY_OWNER_CHECKBOX}
          disabled={isDataLoading}
          label="For Sale by Owner"
          name="forSaleByOwner"
          value={entitySideData.forSaleByOwner}
          onChange={onForSaleByOwnerChange}
        />
      </Box>

      <Box sx={sectionStyles}>
        <TableCDS
          isHideTableWhenEmpty
          additionalRowContent={sellingAgentRowContent}
          additionalRowContentRowId={editableSellingAgentData?.id}
          config={sellingAgentTableConfig}
          customStyles={entitySideDataTableStyles}
          data={correctedSellingAgentsTableData}
          isCustomDataDefinition={!isSellingAgentsExist}
          isLoading={isDataLoading}
          isSortDisabled={isEditSellingAgentActive}
          order={sellingAgentOrder}
          pendingChangesTextSelector="span"
          title="Selling Agent Info (Buyer's Agent)"
          wrapperStyle={tableWrapperStyles}
          onRequestSort={onRequestSellingAgentSort}
        />

        {!isDataLoading && (
          <Box sx={addAnotherEntityStyles}>
            <Button
              data-testid={PropertyAndLoanTestIds.AGENT_AND_SELLER_ADD_SELLING_AGENT_BUTTON}
              data-type={TableDataType.SELLING_AGENT}
              disabled={isEditSellingAgentActive || isLoading || isSellingAgentsMaxCount}
              size="medium"
              startIcon={<AddBoxIcon size={IconSize[20]} />}
              variant="outlined"
              onClick={addNewTableEntity}
            >
              Add Selling Agent
            </Button>
          </Box>
        )}
      </Box>

      <Box sx={sectionStyles}>
        <TableCDS
          isHideTableWhenEmpty
          additionalRowContent={listingAgentRowContent}
          additionalRowContentRowId={editableListingAgentData?.id}
          config={listingAgentTableConfig}
          customStyles={entitySideDataTableStyles}
          data={correctedListingAgentsTableData}
          isCustomDataDefinition={!isListingAgentsExist}
          isLoading={isDataLoading}
          isSortDisabled={isEditListingAgentActive}
          order={listingAgentOrder}
          pendingChangesTextSelector="span"
          title="Listing Agent Info"
          wrapperStyle={tableWrapperStyles}
          onRequestSort={onRequestListingAgentSort}
        />

        {!isDataLoading && (
          <Box sx={addAnotherEntityStyles}>
            <Button
              data-testid={PropertyAndLoanTestIds.AGENT_AND_SELLER_ADD_LISTING_AGENT_BUTTON}
              data-type={TableDataType.LISTING_AGENT}
              disabled={isEditListingAgentActive || isLoading || isListingAgentsMaxCount}
              size="medium"
              startIcon={<AddBoxIcon size={IconSize[20]} />}
              variant="outlined"
              onClick={addNewTableEntity}
            >
              Add Listing Agent
            </Button>
          </Box>
        )}
      </Box>

      <Box sx={sectionStyles}>
        <TableCDS
          isHideTableWhenEmpty
          additionalRowContent={sellerRowContent}
          additionalRowContentRowId={editableSellerData?.id}
          config={sellerTableConfig}
          customStyles={entitySideDataTableStyles}
          data={correctedSellersTableData}
          isCustomDataDefinition={!isSellersExist}
          isLoading={isDataLoading}
          isSortDisabled={isEditSellerActive}
          order={sellerOrder}
          pendingChangesTextSelector="span"
          title="Seller Info"
          wrapperStyle={tableWrapperStyles}
          onRequestSort={onRequestSellerSort}
        />

        {!isDataLoading && (
          <Box sx={addAnotherEntityStyles}>
            <Button
              data-testid={PropertyAndLoanTestIds.AGENT_AND_SELLER_ADD_SELLER_BUTTON}
              data-type={TableDataType.SELLER}
              disabled={isEditSellerActive || isLoading}
              size="medium"
              startIcon={<AddBoxIcon size={IconSize[20]} />}
              variant="outlined"
              onClick={addNewTableEntity}
            >
              Add Seller Info
            </Button>
          </Box>
        )}
      </Box>
    </Box>
  );
};

AgentAndSeller.propTypes = pagesPropTypes;

export default memo(AgentAndSeller);
